import React, { useEffect, useRef, useState } from "react";
import TextField from "components/crm/ui/TextField";

// The area grows with the text, without a scrollbar.
const grow = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

/**
 * A text read and edited in place, at any time (a task description, an appointment report): it is
 * saved on leaving the field, or when the panel closes during typing. `onSave(text)` saves (and
 * shows the toast); `onDraft` follows the typing letter by letter. When `onSave` returns a
 * promise that rejects, the text is not saved: the next blur, or the unmount, tries again.
 */
const AutosaveField = ({
  id,
  label,
  placeholder,
  initial = "",
  rows = 3,
  maxLength,
  onSave,
  onDraft,
}) => {
  const [draft, setDraft] = useState(initial);
  const latest = useRef(draft);
  const saved = useRef(draft.trim());
  // The unmount save must call the latest onSave, not the one of the first render.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  function save() {
    const value = latest.current.trim();
    if (value === saved.current) return;
    const previous = saved.current;
    saved.current = value;
    const result = onSaveRef.current(value);
    if (typeof result?.then === "function")
      result.then(undefined, () => {
        // A later text may have been saved meanwhile: only this one is undone.
        if (saved.current === value) saved.current = previous;
      });
  }
  // Runs once, on unmount: everything it reads goes through refs.
  useEffect(
    () => () => {
      save();
    },
    []
  );
  return (
    <TextField
      id={id}
      label={label}
      multiline
      rows={rows}
      value={draft}
      placeholder={placeholder}
      maxLength={maxLength}
      inputRef={grow}
      onBlur={save}
      onChange={(e) => {
        latest.current = e.target.value;
        setDraft(e.target.value);
        onDraft?.(e.target.value);
        grow(e.target);
      }}
    />
  );
};

export default AutosaveField;
